import { Link, createFileRoute } from "@tanstack/react-router";
import { Button } from "@/components/ui/button";
import { MessageSquare } from "lucide-react";

/**
 * 404 Not Found page component.
 * Displays a friendly error message and provides navigation options.
 * @returns {React.ReactElement} The rendered 404 page component.
 */
function NotFound() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-background/80 flex flex-col">
      {/* Header */}
      <header className="container flex h-14 items-center">
        <Link
          to="/"
          className="flex items-center gap-2 font-semibold hover:opacity-90 transition-opacity"
        >
          <MessageSquare className="h-6 w-6" />
          <span>aiEmailAgent</span>
        </Link>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex items-center justify-center">
        <div className="container max-w-[64rem] flex flex-col items-center text-center gap-8 py-12">
          <div className="space-y-4">
            <h1 className="text-4xl sm:text-6xl font-bold tracking-tighter">
              404 - Page Not Found
            </h1>
            <p className="text-xl text-muted-foreground max-w-[42rem] mx-auto">
              Oops! The page you're looking for seems to have wandered off.
              Let's get you back on track.
            </p>
          </div>

          {/* Glass Card */}
          <div className="glass rounded-xl p-8 shadow-lg border w-full max-w-md">
            <div className="space-y-6">
              <div className="space-y-2">
                <h2 className="text-2xl font-bold">Lost?</h2>
                <p className="text-muted-foreground">
                  Don't worry, we've all been there. Here are some helpful
                  links:
                </p>
              </div>

              <div className="space-y-4">
                <Button asChild className="w-full" size="lg">
                  <Link to="/">Return Home</Link>
                </Button>
                <Button asChild variant="outline" className="w-full" size="lg">
                  <Link to="/dashboard">Go to Dashboard</Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="container py-6 flex items-center">
        <p className="text-sm text-muted-foreground">
          © {new Date().getFullYear()} aiEmailAgent. All rights reserved.
        </p>
      </footer>
    </div>
  );
}

export const Route = createFileRoute("/404")({
  component: NotFound,
});
